import axios from "axios";
import config from "../../config";
import LocalStorageService from "../services/LocalStorageService";
import redirectHome from "../services/redirectHome";
import { notifyTokenRefreshed } from "../services/authTokenEvents";

const axiosClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

// 401s on these endpoints mean bad credentials / an already dead refresh token,
// not an expired access token — never try to refresh or retry them.
const AUTH_EXEMPT_URLS = [
  `${config.API_URL}/auth/`,
  `${config.API_URL}/auth/refresh/`,
];

// The only characters a browser refuses to put in a header value: NUL, LF, CR
// and anything above U+00FF (setRequestHeader takes a ByteString). A token
// holding one of those — e.g. a newline pasted into localStorage — makes the
// request throw before it is sent, so there is no 401 to react to and the
// session must be dropped right away. Everything else (spaces included) is a
// legal header value: send it and let the server answer with a 401, so the
// normal refresh path still gets its chance.
// Spelled out per code unit instead of as a regex: a literal NUL inside a
// character class trips eslint's no-control-regex, and the explicit check
// reads clearer than silencing the rule.
const isSendableCharCode = (code) =>
  code !== 0 && code !== 10 && code !== 13 && code <= 0xff;

const isUsableToken = (token) => {
  if (typeof token !== "string") {
    return false;
  }
  for (let i = 0; i < token.length; i += 1) {
    if (!isSendableCharCode(token.charCodeAt(i))) {
      return false;
    }
  }
  return true;
};

let refreshPromise = null; // single-flight: concurrent 401s share one refresh call

const getStoredAccess = () =>
  LocalStorageService.get("authorizationData")?.access;

const logout = () => {
  const hadSession = !!LocalStorageService.get("authorizationData");
  LocalStorageService.remove("authorizationData");
  LocalStorageService.remove("has_cashier_entered_desk_number");
  // Without leaving the page the header would still render "Log Out", since
  // redux read its auth state from storage at module load. Skipped when there
  // was no session — a 401 on an anonymous page would otherwise reload forever.
  if (hadSession) {
    redirectHome();
  }
};

export const refreshTokens = () => {
  if (!refreshPromise) {
    const refresh = LocalStorageService.get("authorizationData")?.refresh;
    // Bare axios so the call never re-enters axiosClient's interceptors.
    refreshPromise = axios
      .post(
        `${config.API_URL}/auth/refresh/`,
        { refresh },
        { withCredentials: true }
      )
      .then((res) => {
        // The backend rotates the refresh token (single-use), so both fields
        // must be saved. Update them individually — authorizationData also
        // holds groups / group_permissions read by RolesHelper.
        LocalStorageService.updateObject(
          "authorizationData",
          "access",
          res.data.access
        );
        LocalStorageService.updateObject(
          "authorizationData",
          "refresh",
          res.data.refresh
        );
        // The shop websocket authenticates with the access token it was
        // opened with, so it has to reconnect with the new one.
        notifyTokenRefreshed();
        return res.data.access;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

axiosClient.interceptors.request.use(
  (request) => {
    const token = LocalStorageService.get("authorizationData")?.access;
    if (request.url === `${config.API_URL}/images/` || request.url === `${config.API_URL}/patients/scan-id/`) {
      request.headers["Content-Type"] = "multipart/form-data";
    }
    if (token && !isUsableToken(token)) {
      logout();
      return Promise.reject(
        new Error("Stored access token is malformed, logging out")
      );
    }
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
      // Remember the exact token this request was sent with so the response
      // interceptor can detect cross-tab rotation without parsing headers.
      request._accessToken = token;
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => {
    if (response === undefined) {
      return Promise.reject(
        new Error("Incorrect response format, the server might be unavailable")
      );
    }
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      const isExempt = AUTH_EXEMPT_URLS.includes(originalRequest?.url);

      if (originalRequest && !originalRequest._retry && !isExempt) {
        originalRequest._retry = true; // at most one retry per request

        const failedToken = originalRequest._accessToken;
        const authData = LocalStorageService.get("authorizationData");

        if (
          authData?.access &&
          failedToken &&
          authData.access !== failedToken
        ) {
          // Another tab already refreshed — just retry with the new token.
          return axiosClient(originalRequest);
        }

        if (authData?.refresh) {
          try {
            await refreshTokens();
            return axiosClient(originalRequest);
          } catch (refreshError) {
            // Another tab may have consumed the single-use refresh token
            // while our call was in flight — re-check before giving up.
            const latestAccess = getStoredAccess();
            if (latestAccess && latestAccess !== failedToken) {
              return axiosClient(originalRequest);
            }
            logout();
          }
        } else {
          logout();
        }
      } else if (!isExempt) {
        // The retried request 401'd again — the session is genuinely dead.
        logout();
      }
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
